import { initRollbar } from './main/Rollbar/RollbarInit';
import Usercentrics from './main/Usercentrics/Usercentrics';

/**
 * Bootstrap Rollbar with consent-aware configuration.
 * Rollbar is initialized ONCE on page load, then reconfigured when consent changes.
 * This prevents duplicate error reporting by avoiding multiple global error handlers.
 */
(function bootstrapRollbar() {
    try {
        const config = window.ROLLBAR_CONFIG;

        if (!config) {
            return;
        }

        if (!config.enabled || !config.accessToken) {
            return;
        }

        // Enable debug mode via localStorage or URL parameter
        const debugMode = localStorage.getItem('rollbar_debug') === 'true' ||
            window.location.search.includes('rollbar_debug=true');

        const debug = debugMode ? console.log.bind(console) : () => {};

        let rollbarInstance = null;
        let currentConsent = null;

        /**
         * Get configuration based on consent status.
         * Returns complete Rollbar config object with consent-specific settings.
         *
         * Critical config keys when switching modes:
         * - captureIp: true vs 'anonymize'
         * - transform: undefined (disabled) vs function (remove IPs)
         * - autoInstrument: object vs false
         */
        function getConsentConfig(hasConsent) {
            const baseConfig = {
                accessToken: config.accessToken,
                captureUncaught: true,
                captureUnhandledRejections: true,
                reportLevel: 'error',
                payload: {
                    environment: config.environment,
                    client: {
                        javascript: {
                            source_map_enabled: true,
                            code_version: config.instance,
                        },
                    },
                    instance: config.instance,
                },
            };

            if (hasConsent) {
                // Full tracking mode: capture all data including IP and telemetry
                return {
                    ...baseConfig,
                    captureIp: true,
                    captureDeviceInfo: true,
                    captureTelemetry: true,
                    autoInstrument: {
                        log: true,
                        network: true,
                        dom: true,
                        navigation: true,
                        connectivity: true,
                        contentSecurityPolicy: true,
                        errorOnContentSecurityPolicy: true,
                    },
                    scrubPaths: [],
                    scrubFields: [],
                    transform: undefined, // Explicitly disable transform
                };
            }
            // Anonymous mode: scrub all personal data
            return {
                ...baseConfig,
                captureIp: 'anonymize',
                captureTelemetry: false,
                autoInstrument: false,
                captureDeviceInfo: false,
                scrubPaths: [ 'request.query_string' ],
                scrubFields: [ 'query_string' ],
                transform: function (payload) {
                    // Remove IP addresses completely
                    if (payload.request && payload.request.user_ip) {
                        delete payload.request.user_ip;
                    }
                    if (payload.client && payload.client.user_ip) {
                        delete payload.client.user_ip;
                    }
                },
            };
        }

        /**
         * Initialize or reconfigure Rollbar based on consent.
         * First call creates the instance, subsequent calls reconfigure it.
         */
        function updateRollbarConfig() {
            const hasConsent = Usercentrics.isRollbarConsentAllowed();

            // Skip if consent hasn't changed (prevents unnecessary reconfiguration)
            if (currentConsent === hasConsent && rollbarInstance) {
                debug('[rollbar-init] Consent unchanged, skipping update');
                return;
            }

            debug('[rollbar-init] Updating Rollbar config with consent:', hasConsent);
            currentConsent = hasConsent;

            if (!rollbarInstance) {
                // First initialization - create instance
                debug('[rollbar-init] First initialization');
                rollbarInstance = initRollbar(hasConsent, {
                    accessToken: config.accessToken,
                    environment: config.environment,
                    instance: config.instance,
                }, false);
                window.rollbar = rollbarInstance;
            }
            else {
                // Reconfigure existing instance - DO NOT create new instance to avoid duplicate handlers
                debug('[rollbar-init] Reconfiguring existing instance');
                try {
                    const newConfig = getConsentConfig(hasConsent);
                    rollbarInstance.configure(newConfig);
                    debug('[rollbar-init] Reconfiguration successful');
                }
                catch (e) {
                    debug('[rollbar-init] Reconfiguration failed:', e);
                }
            }
        }

        /**
         * Wait for Usercentrics to load and consent data to be available.
         * Usercentrics v3 stores consent in localStorage['ucData'].
         * We poll until both window.__ucCmp AND ucData are available, or timeout after 5 seconds.
         */
        function waitForUsercentrics(callback, maxAttempts = 50, interval = 100) {
            let attempts = 0;

            function check() {
                attempts++;

                // Check if Usercentrics CMP is loaded AND ucData exists in localStorage
                const hasUcCmp = !!window.__ucCmp;
                const ucData = localStorage.getItem('ucData');

                if (hasUcCmp && ucData) {
                    debug('[rollbar-init] Usercentrics ready after', attempts * interval, 'ms');
                    callback();
                    return;
                }

                // Timeout after maxAttempts (5 seconds)
                if (attempts >= maxAttempts) {
                    debug('[rollbar-init] Timeout waiting for Usercentrics (UC:', hasUcCmp, 'ucData:',
                        !!ucData, '), initializing without consent');
                    callback();
                    return;
                }

                // Try again after interval
                setTimeout(check, interval);
            }

            check();
        }

        /**
         * Listen for Usercentrics consent changes.
         * When consent changes, reconfigure Rollbar immediately without page reload.
         */
        function setupConsentChangeListener() {
            // Listen for storage events (consent changes from other tabs)
            window.addEventListener('storage', function (e) {
                if (e.key === 'ucData' && e.newValue) {
                    debug('[rollbar-init] Consent changed (storage event), reconfiguring');
                    setTimeout(updateRollbarConfig, 50);
                }
            });

            // Listen for Usercentrics CMP events (consent changes in current tab)
            window.addEventListener('UC_UI_CMP_EVENT', function (event) {
                if (event && event.detail && event.detail.type) {
                    const eventType = event.detail.type;
                    debug('[rollbar-init] Usercentrics CMP event:', eventType);

                    // Reconfigure on consent change events
                    // SAVE: User saved custom preferences
                    // ACCEPT_ALL: User clicked "Accept All"
                    // DENY_ALL: User clicked "Deny All"
                    if (eventType === 'SAVE' || eventType === 'ACCEPT_ALL' || eventType === 'DENY_ALL') {
                        debug('[rollbar-init] Consent changed, reconfiguring');
                        // Wait for localStorage to be updated
                        setTimeout(updateRollbarConfig, 100);
                    }
                }
            });
        }

        // Initial setup: wait for Usercentrics to be ready, then initialize
        waitForUsercentrics(function () {
            updateRollbarConfig();
            setupConsentChangeListener();
        });
    }
    catch (e) {
        // Silent fail - Rollbar initialization errors should not break the site
        console.error('[rollbar-init] Error initializing Rollbar:', e);
    }
})();
